import {
  cropTypeTranslate,
  taskPriorityTranslate,
  taskStatusTranslate,
  taskTypeTranslate,
} from "@/utils/translations";
import {
  dateOptionToDateValue,
  dateStringToDateValue,
  isoDateToBrazilianDate,
  isoDateToBrazilianDateTime,
} from "@/utils/date";
import {
  IFormattedTaskView,
  ITaskSummaryView,
  ITaskView,
  ITaskViewsList,
  ITaskViewsParams,
  ITaskViewURLParams,
} from "@/@types/manualAnalysis/panel";
import { ManualAnalysisPanelState, RootState } from "@/@types/vuex";

import { ActionTree } from "vuex";
import { Client } from "@/@types/client";
import { DateOption } from "@/constants/manual-analysis-panel/filterParams";
import { IAnalyst } from "@/@types/analyst";
import { IUser } from "@/@types/user";
import { LegalEntity } from "@/services/legalEntity/schemas/legalEntity";
import manualAnalysisPanelService from "@/services/manualAnalysisPanel";
import { NULL_STRING } from "@/constants/shared/strings";
import { seasonStringToSeason } from "@/utils/string";
import { TaskPrioritySortable } from "@/constants/manual-analysis/shared";

const actions: ActionTree<ManualAnalysisPanelState, RootState> = {
  async formatTaskView(
    { rootGetters },
    tasks: ITaskViewsList,
  ): Promise<ITaskViewsList> {
    const clientById = rootGetters["client/clientById"];
    const analystById = rootGetters["analyst/analystById"];
    const userById = rootGetters["user/userById"];
    const entityById = rootGetters["legalEntity/getEntityById"];

    const formatTasks = (tasks: ITaskView[]): IFormattedTaskView[] =>
      tasks.map((task) =>
        _formatTask(task, clientById, userById, analystById, entityById),
      );

    return {
      DOING: formatTasks(tasks.DOING),
      DONE: formatTasks(tasks.DONE),
      REVISIONS: formatTasks(tasks.REVISIONS),
      TODO: formatTasks(tasks.TODO),
    };
  },
  async getTaskViews(
    { dispatch },
    payload: ITaskViewURLParams & {
      signal?: AbortSignal;
      page?: number;
      search?: string;
      size?: number;
      tab?: string;
      sortBy?: string;
      sortOrder?: string;
    },
  ): Promise<ITaskSummaryView> {
    const getDateFilters = (dateType = DateOption.THIS_WEEK, dateValue?: string) => {
      if (dateType !== DateOption.SPECIFIC_DATES) {
        return dateOptionToDateValue(dateType);
      }
      if (dateValue) return dateStringToDateValue(dateValue);
      return dateOptionToDateValue(DateOption.THIS_WEEK);
    };

    const {
      dueDateValue,
      dueDateType,
      season,
      signal,
      page,
      search,
      size,
      tab,
      sortBy,
      sortOrder,
      ...rest
    } = payload;
    const dates = getDateFilters(dueDateType as DateOption, dueDateValue);
    const seasonCrop = seasonStringToSeason(season || "");
    const seasonCropOnly = !seasonCrop && season ? { crop: season.split(";")[0] } : {};
    const params = {
      ...dates,
      ...seasonCrop,
      ...seasonCropOnly,
      ...rest,
      ...(page != null && { page, size }),
      ...(tab && { tab }),
      ...(sortBy && { sortBy, sortOrder }),
      ...(search && { search }),
    } as ITaskViewsParams;

    const { tasks, summary, pagination } =
      await manualAnalysisPanelService.getTaskViews(params, signal);

    const taskViews: ITaskSummaryView = {
      summary,
      tasks: await dispatch("formatTaskView", tasks),
      ...(pagination && { pagination }),
    };

    return Object.freeze(taskViews);
  },
};

const _formatTask = (
  task: ITaskView,
  clientById: Function,
  userById: Function,
  analystById: Function,
  entityById: Function,
): IFormattedTaskView => {
  const {
    analystId,
    analystName,
    clientId,
    creationTime,
    completionTime,
    startTime,
    dueDate,
    financedCrop,
    season,
    taskStatus,
    taskType,
    serialId,
    priority,
    municipality,
    state,
    producerId,
    producerName,
  } = task;

  const client: Client | undefined = clientById(clientId);

  const resolvedAnalystName = (() => {
    if (analystName) return analystName;
    const analyst: IAnalyst | undefined = analystById(analystId);
    const analystUser: IUser | undefined = userById(analyst?.userId);
    return analystUser?.name || NULL_STRING;
  })();

  const resolvedProducerName = (() => {
    if (producerName) return producerName;
    const producer: LegalEntity | undefined = producerId
      ? entityById(producerId)
      : undefined;
    return producer?.name ?? "Desconhecido";
  })();

  const formattedTaskView = {
    ...task,
    analystName: resolvedAnalystName,
    clientName: client?.pretty_name || client?.name || NULL_STRING,
    producerName: resolvedProducerName,
    serialId: serialId || NULL_STRING,
    sortablePriority: TaskPrioritySortable[priority],
    translatedCompletionTime: isoDateToBrazilianDateTime(completionTime),
    translatedCreationTime: isoDateToBrazilianDateTime(creationTime),
    translatedDueDate: isoDateToBrazilianDate(dueDate),
    translatedFinancedCrop: cropTypeTranslate(financedCrop),
    translatedLocation: `${municipality} - ${state}`,
    translatedPriority: taskPriorityTranslate(priority),
    translatedSeason: `${season.crop} ${season.year}`,
    translatedStartTime: isoDateToBrazilianDateTime(startTime),
    translatedTaskStatus: taskStatusTranslate(taskStatus),
    translatedTaskType: taskTypeTranslate(taskType),
  };

  return formattedTaskView;
};

export default actions;
